import {ApiUrls} from 'shared/config/model/urls';

import {createApi} from '@reduxjs/toolkit/query/react';

import {AxiosRequestHeaders} from 'axios';

import httpBaseQuery from 'shared/api/rtk-query/httpBaseQuery';
import {ICity} from 'entities/location';
import {buildQuery} from 'shared/api/query';
import IPagination from 'shared/api/model/IPagination';
import IHydraResponse from 'shared/api/model/HydraResponse';
import {providesList} from 'shared/api/rtk-query/RTKQueryHelper';
import {paginationAdapter} from 'shared/api/paginationHelper';
import ICollectionParams from 'shared/api/model/ICollectionParams';

interface ICreateCityRequest {
    name: string;
    country: string;
}

interface IUpdateCityRequest {
    id: number;
    name: string;
    country: string;
}

export const cityAPI = createApi({
    reducerPath: 'cityAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['City'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        getCities: build.query<IPagination<ICity>, ICollectionParams>({
            query: ({page, sort, filters}) => ({
                path: buildQuery(ApiUrls.CITIES(page, sort, filters)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesList(result?.items, 'City'),
            transformResponse(
                rawResult: IHydraResponse<ICity>,
            ): Promise<IPagination<ICity>> | IPagination<ICity> {
                return paginationAdapter(rawResult);
            },
        }),
        getCity: build.query<ICity[], number>({
            query: (id) => ({
                path: ApiUrls.CITIES_BY_COUNTRY(id),
            }),
            providesTags: (_result, _error, id) => [{ type: 'City', id }],
        }),
        createCity: build.mutation<ICity, ICreateCityRequest>({
            query: (data) => ({
                path: ApiUrls.CITIES(),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['City'],
        }),
        updateCity: build.mutation<ICity, IUpdateCityRequest>({
            query: ({id, ...data}) => ({
                path: ApiUrls.CITY(id),
                method: 'PUT',
                data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'City', id },
                { type: 'City', id: 'LIST' },
            ],
        }),
        deleteCity: build.mutation<void, number>({
            query: (id) => ({
                path: ApiUrls.CITY(id),
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, id) => [
                { type: 'City', id },
                { type: 'City', id: 'LIST' },
            ],
        }),
        searchCities: build.query<ICity[], { countryId: number; query: string }>({
            query: ({ countryId, query }) => ({
                path: ApiUrls.SEARCH_CITIES(countryId, query),
            }),
            providesTags: (result) =>
                result
                    ? [...result.map(({ id }) => ({ type: 'City' as const, id })), 'City']
                    : ['City'],
        }),
    }),
});